// Portable text layout engine.
//
// Pure arithmetic: no fabric, no DOM, no CanvasRenderingContext2D. Glyph
// measurement arrives through the metrics provider adapter documented in
// textMetricsProvider.js, so this file is the piece that gets ported to
// C# / ImageSharp verbatim.
//
//   layoutText({ text, font, boxWidth, boxHeight, options }, metricsProvider)
//     -> null when there is nothing to lay out (empty or whitespace-only text,
//        or a degenerate box), otherwise a layout result whose geometry is
//        expressed in em multiples and box-relative fractions - never in device
//        pixels - so the same result can be scaled to 96 DPI for the editor and
//        300 DPI for print.
//
// Line breaking is owned here. Renderers consume `lines` as an explicit,
// already-computed array and must never re-break it themselves.

const LAYOUT_VERSION = 1;

// every measurement is taken at this size and divided back down to em; ink
// metrics are linear in font size so one measurement serves every box size
const MEASURE_FONT_SIZE = 100;

const DEFAULT_MIN_FONT_SIZE = 6;
const DEFAULT_MAX_FONT_SIZE = 4000;
const DEFAULT_SAFETY_INSET = 0.02;
const DEFAULT_LINE_SPACING_EM = 1.2;
// a candidate whose vertical slack is within this fraction of the fit height
// still counts as filling the height; among those the largest font wins. One
// value shared with the C# engine (TextLayoutDefaults.VerticalSlackTolerance)
const DEFAULT_VERTICAL_SLACK_TOLERANCE = 0.1;
const MAX_INSET = 0.45;
const MAX_WRAP_CANDIDATES = 48;
const LINE_SIGNATURE_SEPARATOR = '\u0000';

const HARD_BREAK_PATTERN = /\r\n|\r|\n/;
const WORD_SEPARATOR_PATTERN = /[ \t\r]/;

const FIT_MODE = {
  INK: 'ink',
  BAND: 'band'
};

const FITTED_AXIS = {
  WIDTH: 'width',
  HEIGHT: 'height'
};

const TEXT_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right'
};

const FALLBACK_PROBE = {
  capHeightEm: 0.7,
  xHeightEm: 0.5,
  ascenderHeightEm: 0.75,
  descenderDepthEm: 0.2,
  lineSpacingEm: DEFAULT_LINE_SPACING_EM
};

const toPositiveNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const toNonNegativeNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const resolveInset = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_SAFETY_INSET;
  }
  return Math.min(Math.max(parsed, 0), MAX_INSET);
};

const resolveTextAlign = (value) => {
  const normalized = typeof value === 'string' ? value.toLowerCase() : '';
  if (normalized === TEXT_ALIGN.LEFT || normalized.startsWith('justify')) {
    return TEXT_ALIGN.LEFT;
  }
  if (normalized === TEXT_ALIGN.RIGHT) {
    return TEXT_ALIGN.RIGHT;
  }
  return TEXT_ALIGN.CENTER;
};

const resolveFitMode = (value) =>
  value === FIT_MODE.BAND ? FIT_MODE.BAND : FIT_MODE.INK;

const layoutText = (
  { text, font, boxWidth, boxHeight, options = {} },
  metricsProvider
) => {
  if (!metricsProvider || !(boxWidth > 0) || !(boxHeight > 0)) {
    return null;
  }

  const resolvedText = typeof text === 'string' ? text : '';
  // empty or whitespace-only text keeps whatever size the box already had
  if (!resolvedText.trim()) {
    return null;
  }

  const fitMode = resolveFitMode(options.fitMode);
  const textAlign = resolveTextAlign(options.textAlign);
  const allowMultiline = !!options.allowMultiline;
  const insetX = resolveInset(options.insetX);
  const insetY = resolveInset(options.insetY);
  const probe = metricsProvider.getFontProbeMetrics
    ? metricsProvider.getFontProbeMetrics(font)
    : FALLBACK_PROBE;

  // line spacing is font-derived and owned here, not inherited from whatever
  // line height the renderer happens to default to; it ships in the result so
  // every renderer lays the same block out. An explicit option still wins.
  const lineSpacingEm = toPositiveNumber(
    options.lineSpacingEm,
    toPositiveNumber(probe.lineSpacingEm, DEFAULT_LINE_SPACING_EM)
  );
  const minFontSize = toPositiveNumber(
    options.minFontSize,
    DEFAULT_MIN_FONT_SIZE
  );
  const maxFontSize = toPositiveNumber(
    options.maxFontSize,
    DEFAULT_MAX_FONT_SIZE
  );
  const strokeWidthEm = toPositiveNumber(options.strokeWidthEm, 0);
  const currentFontSize = toPositiveNumber(options.currentFontSize, 0);
  const verticalSlackTolerance = toNonNegativeNumber(
    options.verticalSlackTolerance,
    DEFAULT_VERTICAL_SLACK_TOLERANCE
  );

  const fitWidth = boxWidth * (1 - insetX * 2);
  const fitHeight = boxHeight * (1 - insetY * 2);
  if (!(fitWidth > 0) || !(fitHeight > 0)) {
    return null;
  }

  const measureEm = (value) => {
    const metrics = metricsProvider.measure(value, {
      ...font,
      fontSizePx: MEASURE_FONT_SIZE
    });

    return {
      advanceEm: metrics.advance / MEASURE_FONT_SIZE,
      inkLeftEm: metrics.inkLeft / MEASURE_FONT_SIZE,
      inkRightEm: metrics.inkRight / MEASURE_FONT_SIZE,
      inkAscentEm: metrics.inkAscent / MEASURE_FONT_SIZE,
      inkDescentEm: metrics.inkDescent / MEASURE_FONT_SIZE
    };
  };

  const spaceAdvanceEm = measureEm(' ').advanceEm;

  const computeFit = (lines) => {
    const lineMetrics = lines.map(measureEm);
    // a wrapped line holding nothing but the whitespace that followed the last
    // word draws no ink, so it must not add a band of empty space below the
    // block - "MIKE " has to size exactly like "MIKE"
    const inkedIndex = lines.reduce(
      (found, line, index) => (line.trim() ? index : found),
      -1
    );
    const lastIndex = inkedIndex >= 0 ? inkedIndex : lineMetrics.length - 1;

    // only the outer bounds of the block use ink; the spacing between lines
    // stays uniform and font-derived
    let topEm =
      fitMode === FIT_MODE.BAND
        ? probe.capHeightEm
        : lineMetrics[0].inkAscentEm;
    let bottomEm =
      fitMode === FIT_MODE.BAND
        ? probe.descenderDepthEm
        : lineMetrics[lastIndex].inkDescentEm;

    // a lone hairline glyph ('.', '-') would otherwise scale to an absurd size;
    // pad it symmetrically so the fit shrinks but the ink stays centred
    if (fitMode === FIT_MODE.INK) {
      const rawBlockHeightEm = topEm + lastIndex * lineSpacingEm + bottomEm;
      if (rawBlockHeightEm < probe.xHeightEm) {
        const padEm = (probe.xHeightEm - rawBlockHeightEm) / 2;
        topEm += padEm;
        bottomEm += padEm;
      }
    }

    const blockHeightEm =
      topEm + lastIndex * lineSpacingEm + bottomEm + strokeWidthEm;
    const blockWidthEm =
      lineMetrics.reduce(
        (widest, metrics) =>
          Math.max(widest, metrics.inkLeftEm + metrics.inkRightEm),
        0
      ) + strokeWidthEm;

    const sizeFromHeight =
      blockHeightEm > 0 ? fitHeight / blockHeightEm : maxFontSize;
    const sizeFromWidth =
      blockWidthEm > 0 ? fitWidth / blockWidthEm : maxFontSize;
    const rawFontSize = Math.min(sizeFromHeight, sizeFromWidth);

    return {
      lineMetrics,
      lastIndex,
      topEm,
      bottomEm,
      blockHeightEm,
      blockWidthEm,
      rawFontSize,
      // the fraction of the fit height this candidate leaves empty; zero when
      // height bound, and the selection tolerance is measured against it
      verticalSlackFraction:
        sizeFromHeight > 0 ? 1 - rawFontSize / sizeFromHeight : 0,
      fittedAxis:
        sizeFromHeight <= sizeFromWidth ? FITTED_AXIS.HEIGHT : FITTED_AXIS.WIDTH
    };
  };

  // explicit hard breaks are forced; auto wrapping only ever happens inside a
  // hard segment, so they set the floor on the resulting line count
  const hardSegments = resolvedText
    .split(HARD_BREAK_PATTERN)
    .map((hardLine) =>
      hardLine
        .split(WORD_SEPARATOR_PATTERN)
        .map((word) => ({ word, advanceEm: measureEm(word).advanceEm }))
    );

  // greedy word wrap; mirrors the renderer semantics so a renderer handed the
  // widest resolved line advance reproduces exactly these breaks. Words are
  // never broken, so a word wider than maxWidthEm simply gets its own line
  const wrapSegments = (maxWidthEm) => {
    const wrapped = [];

    hardSegments.forEach((words) => {
      let line = '';
      let lineWidthEm = 0;
      let lineJustStarted = true;

      words.forEach(({ word, advanceEm }) => {
        const infixWidthEm = lineJustStarted ? 0 : spaceAdvanceEm;
        // grouped exactly like collectWrapWidthsEm's accumulation - a different
        // association differs in the last float bit, and at a candidate width
        // equal to a run's advance that one bit spuriously drops the very line
        // configuration the candidate was generated to produce
        const nextWidthEm = lineWidthEm + (infixWidthEm + advanceEm);

        if (nextWidthEm > maxWidthEm && !lineJustStarted) {
          wrapped.push(line);
          line = word;
          lineWidthEm = advanceEm;
        }
        else {
          line = lineJustStarted ? word : `${line} ${word}`;
          lineWidthEm = nextWidthEm;
        }

        lineJustStarted = false;
      });

      wrapped.push(line);
    });

    return wrapped;
  };

  // every wrap width that can move a break sits at the advance width of some
  // contiguous run of words, so scanning those widths enumerates every line
  // configuration the greedy wrapper can reach - no fixed point iteration
  const collectWrapWidthsEm = () => {
    const widths = new Set();

    hardSegments.forEach((words) => {
      for (let start = 0; start < words.length; start += 1) {
        let runWidthEm = 0;

        for (let end = start; end < words.length; end += 1) {
          runWidthEm +=
            (end === start ? 0 : spaceAdvanceEm) + words[end].advanceEm;
          widths.add(runWidthEm);
        }
      }
    });

    const sorted = [...widths].sort((a, b) => b - a);
    if (sorted.length <= MAX_WRAP_CANDIDATES) {
      return sorted;
    }

    // pathological word counts only: sample the range instead of scanning it
    const stride = sorted.length / MAX_WRAP_CANDIDATES;
    const sampled = [];
    for (let index = 0; index < MAX_WRAP_CANDIDATES; index += 1) {
      sampled.push(sorted[Math.floor(index * stride)]);
    }
    sampled.push(sorted[sorted.length - 1]);

    return sampled;
  };

  // the height is the constraint and the width fill is the objective: among
  // the candidates whose vertical slack stays within tolerance, the largest
  // font wins. Wrapping is the mechanism that makes the width fit, and a
  // nominally width bound candidate stays eligible while it fills the height
  // to within the tolerance. Alongside the lines, the wrap width that produced
  // them is returned so a renderer can reproduce exactly these breaks; the
  // paths that must never re-break return Infinity
  const resolveLines = () => {
    if (!allowMultiline) {
      const hardLines = resolvedText.split(HARD_BREAK_PATTERN);
      return {
        lines: hardLines,
        fit: computeFit(hardLines),
        wrapWidthEm: Infinity
      };
    }

    const evaluated = [];
    const seen = new Set();

    collectWrapWidthsEm().forEach((maxWidthEm) => {
      const candidateLines = wrapSegments(maxWidthEm);
      const signature = candidateLines.join(LINE_SIGNATURE_SEPARATOR);
      if (seen.has(signature)) {
        return;
      }

      seen.add(signature);
      evaluated.push({
        lines: candidateLines,
        fit: computeFit(candidateLines),
        wrapWidthEm: maxWidthEm
      });
    });

    if (evaluated.length === 0) {
      const hardLines = resolvedText.split(HARD_BREAK_PATTERN);
      return {
        lines: hardLines,
        fit: computeFit(hardLines),
        wrapWidthEm: Infinity
      };
    }

    evaluated.sort(
      (a, b) =>
        a.lines.length - b.lines.length ||
        a.fit.blockWidthEm - b.fit.blockWidthEm
    );

    // wrapping must earn its lines: a configuration that uses more lines
    // without producing a larger font than some shorter configuration gains no
    // width fill - it happens when an unbreakable word already forces the
    // block width, and splitting the remaining words just stacks blank bands
    // ("This" / "is" / "gggggggg" never beats "This is" / "gggggggg").
    // Discard those before picking a winner
    const contenders = evaluated.filter(
      (candidate) =>
        !evaluated.some(
          (other) =>
            other.lines.length < candidate.lines.length &&
            other.fit.rawFontSize >= candidate.fit.rawFontSize
        )
    );

    const qualifying = contenders.filter(
      (candidate) =>
        candidate.fit.verticalSlackFraction <= verticalSlackTolerance
    );
    if (qualifying.length > 0) {
      return qualifying.reduce((best, candidate) =>
        candidate.fit.rawFontSize > best.fit.rawFontSize ? candidate : best
      );
    }

    // no contender fills the height to within the tolerance, which happens
    // when a single unbreakable word is too wide: fall back to the largest
    // size any configuration allows and accept the vertical slack
    return contenders.reduce((best, candidate) =>
      candidate.fit.rawFontSize > best.fit.rawFontSize ? candidate : best
    );
  };

  const { lines, fit, wrapWidthEm } = resolveLines();

  let { rawFontSize } = fit;
  // ignore a recalculation that lands within half a pixel of the current size
  // so typing does not make the text jitter
  if (currentFontSize && Math.abs(rawFontSize - currentFontSize) < 0.5) {
    rawFontSize = currentFontSize;
  }

  let fontSize = Math.floor(rawFontSize);
  let overflow = false;
  if (fontSize < minFontSize) {
    fontSize = minFontSize;
    overflow = true;
  }
  else if (fontSize > maxFontSize) {
    fontSize = maxFontSize;
  }
  fontSize = Math.max(1, fontSize);

  const { lineMetrics, lastIndex } = fit;
  const strokeHalfPx = (strokeWidthEm * fontSize) / 2;

  const blockHeightPx = fit.blockHeightEm * fontSize;
  const blockTopPx = (boxHeight - blockHeightPx) / 2;
  const firstBaselinePx = blockTopPx + strokeHalfPx + fit.topEm * fontSize;
  const baselinePxValues = lineMetrics.map(
    (metrics, index) => firstBaselinePx + index * lineSpacingEm * fontSize
  );

  const insetLeftPx = boxWidth * insetX;
  const lineInkWidthsPx = lineMetrics.map(
    (metrics) => (metrics.inkLeftEm + metrics.inkRightEm) * fontSize
  );
  const lineInkLeftPxValues = lineInkWidthsPx.map((inkWidthPx) => {
    if (textAlign === TEXT_ALIGN.LEFT) {
      return insetLeftPx + strokeHalfPx;
    }
    if (textAlign === TEXT_ALIGN.RIGHT) {
      return insetLeftPx + fitWidth - inkWidthPx - strokeHalfPx;
    }
    return insetLeftPx + (fitWidth - inkWidthPx) / 2;
  });

  const inkLeftPx = Math.min(...lineInkLeftPxValues);
  const inkRightPx = Math.max(
    ...lineInkLeftPxValues.map(
      (leftPx, index) => leftPx + lineInkWidthsPx[index]
    )
  );
  const inkTopPx = firstBaselinePx - lineMetrics[0].inkAscentEm * fontSize;
  const inkBottomPx =
    baselinePxValues[lastIndex] +
    lineMetrics[lastIndex].inkDescentEm * fontSize;

  return {
    version: LAYOUT_VERSION,
    fontSize,
    lines,
    lineAdvances: lineMetrics.map((metrics) => metrics.advanceEm),
    lineMetrics,
    lineSpacingEm,
    wrapWidthEm,
    baselines: baselinePxValues.map((baselinePx) => baselinePx / boxHeight),
    lineInkLeft: lineInkLeftPxValues.map((leftPx) => leftPx / boxWidth),
    inkBox: {
      left: inkLeftPx / boxWidth,
      top: inkTopPx / boxHeight,
      width: (inkRightPx - inkLeftPx) / boxWidth,
      height: (inkBottomPx - inkTopPx) / boxHeight
    },
    fittedAxis: fit.fittedAxis,
    overflow,
    fitMode,
    textAlign,
    allowMultiline,
    insetX,
    insetY
  };
};

export {
  layoutText,
  FIT_MODE,
  FITTED_AXIS,
  TEXT_ALIGN,
  LAYOUT_VERSION,
  MEASURE_FONT_SIZE,
  DEFAULT_SAFETY_INSET
};
